import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerPostingTemplateCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { PostingTemplateDto } from "@/features/ledger/api/dto";

/**
 * Active un modèle d'imputation (`POST /PostingTemplates/{id}/activate`) —
 * réservé à flashpay-superadmin / flashpay-admin. Un seul modèle actif à
 * la fois par couple code produit / provider : le précédent est archivé.
 */
export const useActivatePostingTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "posting-templates", "activate"],
    mutationFn: (postingTemplateId: string) =>
      mutateData({
        url: ledgerEndpoints.postingTemplates.activate(postingTemplateId),
        method: "POST",
      }) as Promise<PostingTemplateDto>,
    onSuccess: (_data, postingTemplateId) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerPostingTemplateCacheKeys(postingTemplateId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useActivatePostingTemplate failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
